import type { Insets } from "./target-frame"

// How the camera screen is laid out: upright, the instruction on top and
// the controls at the bottom; sideways (the best way to photograph a whole
// car: about twice the pixels on it), the instruction in a column on one
// side and the controls in a rail on the side the phone's bottom is, like
// the phone's own camera. Pure; unit-tested.

export interface CameraLayout {
  landscape: boolean
  // Where the shutter rail goes when sideways.
  controls: "bottom" | "right" | "left"
}

// How the phone is held, from what the browser knows, best first:
//   type   screen.orientation.type (most browsers; iOS 16.4+)
//   angle  window.orientation, 0 / 180 / ±90 (older iPhones)
//   w, h   the page's size, last: an on-screen keyboard shrinks the height
//          and would make an upright phone look sideways.
// Without a side (no type), a sideways phone gets the controls on the right.
export function cameraLayout(
  w: number,
  h: number,
  type?: string | null,
  angle?: number | null,
): CameraLayout {
  const landscape = type
    ? type.startsWith("landscape")
    : typeof angle === "number"
      ? Math.abs(angle) === 90
      : w > h
  if (!landscape) return { landscape: false, controls: "bottom" }
  const left = type ? type === "landscape-secondary" : angle === -90
  return { landscape: true, controls: left ? "left" : "right" }
}

export interface Rect {
  left: number
  top: number
  right: number
  bottom: number
}

// What the controls cover, side by side: a strip across most of the width
// covers the top or the bottom; one down most of the height covers a side.
// (The target for the whole car goes in what's left: target-frame.ts.)
export function insetsFrom(root: Rect, covers: readonly Rect[]): Insets {
  const W = root.right - root.left
  const H = root.bottom - root.top
  const out: Insets = { top: 0, right: 0, bottom: 0, left: 0 }
  for (const r of covers) {
    const w = r.right - r.left
    const h = r.bottom - r.top
    if (w <= 0 || h <= 0) continue
    const cx = (r.left + r.right) / 2 - root.left
    const cy = (r.top + r.bottom) / 2 - root.top
    if (w >= W * 0.6) {
      if (cy < H / 2) out.top = Math.max(out.top, r.bottom - root.top)
      else out.bottom = Math.max(out.bottom, root.bottom - r.top)
    } else if (h >= H * 0.6) {
      if (cx < W / 2) out.left = Math.max(out.left, r.right - root.left)
      else out.right = Math.max(out.right, root.right - r.left)
    }
  }
  return {
    top: Math.round(out.top),
    right: Math.round(out.right),
    bottom: Math.round(out.bottom),
    left: Math.round(out.left),
  }
}
